import {
  NOTIFICATION_FILTER_READ,
  NOTIFICATION_FILTER_UNREAD,
  NOTIFICATION_TYPE_UNKNOWN,
} from "@/features/notifications/utils/notification-display";
import type { GetNotificationResponse } from "@/openapi/generated";

export type NotificationReadStatus =
  | ""
  | typeof NOTIFICATION_FILTER_UNREAD
  | typeof NOTIFICATION_FILTER_READ;

export interface NotificationFiltersState {
  readStatus: NotificationReadStatus;
  notificationType: string;
}

export type NotificationFilterChipKey =
  | "readStatus"
  | "notificationType"
  | "quickSearch";

export const EMPTY_NOTIFICATION_FILTERS: NotificationFiltersState = {
  readStatus: "",
  notificationType: "",
};

export const NOTIFICATION_TYPE_FILTER_OPTIONS: {
  label: string;
  value: string;
}[] = [
  { label: "All types", value: "" },
  { label: "Unknown", value: NOTIFICATION_TYPE_UNKNOWN },
  { label: "Payment", value: "payment" },
  { label: "Enrollment", value: "enroll" },
  { label: "Class", value: "class" },
  { label: "Attendance", value: "attendance" },
];

export function getNotificationTypeFilterOptions(hideFamilyTypes = false): {
  label: string;
  value: string;
}[] {
  if (!hideFamilyTypes) return NOTIFICATION_TYPE_FILTER_OPTIONS;
  return NOTIFICATION_TYPE_FILTER_OPTIONS.filter(
    (option) => option.value !== "payment" && option.value !== "enroll",
  );
}

export function notificationFiltersEqual(
  a: NotificationFiltersState,
  b: NotificationFiltersState,
): boolean {
  return (
    a.readStatus === b.readStatus && a.notificationType === b.notificationType
  );
}

export function countNotificationModalFilters(
  filters: NotificationFiltersState,
): number {
  let count = 0;
  if (filters.readStatus) count++;
  if (filters.notificationType) count++;
  return count;
}

export function hasActiveNotificationSearch(
  quickSearch: string,
  filters: NotificationFiltersState,
): boolean {
  return (
    quickSearch.trim() !== "" || countNotificationModalFilters(filters) > 0
  );
}

/** List scroll uses the general notifications endpoint; read status is applied client-side. */
export function usesUnreadNotificationsEndpoint(
  _filters: NotificationFiltersState,
): boolean {
  return false;
}

export function hasClientOnlyNotificationFilters(
  quickSearch: string,
  filters: NotificationFiltersState,
): boolean {
  return (
    quickSearch.trim() !== "" ||
    filters.readStatus === NOTIFICATION_FILTER_UNREAD ||
    filters.readStatus === NOTIFICATION_FILTER_READ ||
    !!filters.notificationType
  );
}

export function matchesNotificationTypeFilter(
  type: string | null | undefined,
  filterValue: string,
): boolean {
  if (!filterValue) return true;
  if (filterValue === NOTIFICATION_TYPE_UNKNOWN) return !type;
  const normalized = (type ?? "").toLowerCase();
  return normalized.includes(filterValue.toLowerCase());
}

export function matchesNotificationQuickSearch(
  notification: Pick<GetNotificationResponse, "title" | "message">,
  quickSearch: string,
): boolean {
  const q = quickSearch.trim().toLowerCase();
  if (!q) return true;
  return (
    (notification.title?.toLowerCase().includes(q) ?? false) ||
    (notification.message?.toLowerCase().includes(q) ?? false)
  );
}

export function applyClientNotificationFilters<
  T extends Pick<
    GetNotificationResponse,
    "title" | "message" | "type" | "isRead"
  >,
>(
  notifications: T[],
  quickSearch: string,
  filters: NotificationFiltersState,
): T[] {
  let filtered = notifications;

  if (filters.readStatus === NOTIFICATION_FILTER_UNREAD) {
    filtered = filtered.filter((n) => !n.isRead);
  }

  if (filters.readStatus === NOTIFICATION_FILTER_READ) {
    filtered = filtered.filter((n) => n.isRead);
  }

  if (filters.notificationType) {
    filtered = filtered.filter((n) =>
      matchesNotificationTypeFilter(n.type, filters.notificationType),
    );
  }

  if (quickSearch.trim()) {
    filtered = filtered.filter((n) =>
      matchesNotificationQuickSearch(n, quickSearch),
    );
  }

  return filtered;
}

export function getNotificationReadStatusLabel(
  readStatus: NotificationReadStatus,
): string {
  if (readStatus === NOTIFICATION_FILTER_UNREAD) return "Unread";
  if (readStatus === NOTIFICATION_FILTER_READ) return "Read";
  return "All";
}

export function getNotificationTypeFilterLabel(typeValue: string): string {
  return (
    NOTIFICATION_TYPE_FILTER_OPTIONS.find((o) => o.value === typeValue)
      ?.label ?? "Type filter"
  );
}

function countActiveNotificationCriteria(
  quickSearch: string,
  filters: NotificationFiltersState,
): number {
  let count = 0;
  if (quickSearch.trim()) count++;
  if (filters.readStatus) count++;
  if (filters.notificationType) count++;
  return count;
}

export function formatNotificationsListDescription(
  totalCount: number,
  quickSearch: string,
  filters: NotificationFiltersState,
): string {
  const matchWord = totalCount === 1 ? "notification" : "notifications";
  const criteriaCount = countActiveNotificationCriteria(quickSearch, filters);
  const quickQuery = quickSearch.trim();

  if (criteriaCount === 0) {
    return `${totalCount} ${matchWord}`;
  }

  if (quickQuery && criteriaCount === 1) {
    return `${totalCount} ${matchWord} matching “${quickQuery}”`;
  }

  if (quickQuery) {
    return `${totalCount} ${matchWord} matching “${quickQuery}” and ${criteriaCount - 1} other ${
      criteriaCount - 1 === 1 ? "criterion" : "criteria"
    }`;
  }

  return `${totalCount} ${matchWord} matching ${criteriaCount} search ${
    criteriaCount === 1 ? "criterion" : "criteria"
  }`;
}
